import { useMemo } from 'react';
import {
  autoMatchColumn,
  SYSTEM_FIELDS,
  SYSTEM_FIELD_LABELS,
  type SystemField,
} from './autoMatchColumn';
import type { FormType } from '../../types/franchiseLocationProfile';

// "Map columns + set defaults" — the new step in the bulk upload
// flow, between file pick and preview. Round-6 lands Table 1 (XLS
// column → system field); Table 2 (bulk-set enum defaults) follows
// in the next commit.
//
// State shape: the component is controlled by its parent. The parent
// keeps a Record<XlsHeader, SystemField | 'skip'> — one entry per
// column in the sheet, keyed by the original XLS header text. This
// shape pairs naturally with the rendered table (one row per
// header) and inverts cleanly into the applyMappingAndBulkSet input
// via invertColumnMapping.
//
// Same-field-picked-twice rule: last-wins-with-silent-revert. When
// the AM picks SystemField X for header A then later picks X for
// header B, A's mapping flips to 'skip' automatically. The dropdowns
// update visibly, no toast or modal. Keeps the state machine
// deterministic.

export type ColumnMappingState = Record<string, SystemField | 'skip'>;

// Build the initial mapping from a header list by running each
// header through autoMatchColumn. Applies the same last-wins rule
// the manual UI uses — when two headers auto-match to the same
// field, the LAST header (rightmost in the sheet) keeps its match
// and earlier ones revert to 'skip'. Avoids starting the form in a
// pre-loaded conflict state.
export function initialMappingFor(headers: string[]): ColumnMappingState {
  const seedRaw: ColumnMappingState = {};
  for (const h of headers) seedRaw[h] = autoMatchColumn(h);
  // Walk right-to-left: first occurrence (from the right) wins; any
  // earlier header that collided on the same field clears to 'skip'.
  const taken = new Set<SystemField>();
  for (let i = headers.length - 1; i >= 0; i--) {
    const h = headers[i]!;
    const f = seedRaw[h];
    if (f === undefined || f === 'skip') continue;
    if (taken.has(f)) {
      seedRaw[h] = 'skip';
    } else {
      taken.add(f);
    }
  }
  return seedRaw;
}

// Bulk-set picks for enum-shaped fields the AM wants to apply
// uniformly across every imported row. Distinct from Table 1's
// per-column mapping — these fields aren't sourced from the XLS at
// all; the AM picks one value that lands on every row identically.
//
// Field choice (round-6 cap): defaultFormType + defaultMetaAdType.
// Brand stays implicit from the route, Status / Currency keep their
// server-side defaults on Account (not on ImportRow). If a future
// XLS sheet ever carries a per-row defaultFormType column, the
// resolution is to add that field to SYSTEM_FIELDS so it can be
// mapped via Table 1 — Table 2's contents stay enum-only.
export type BulkSetState = {
  defaultFormType?: FormType;
  defaultMetaAdType?: 'traffic' | 'lead_gen';
};

// Friendly labels for the Table-2 dropdowns. Kept inline rather
// than imported because the canonical labels live on the
// FranchiseLocationProfile authoring UI elsewhere; centralising would
// be premature.
const FORM_TYPE_OPTIONS: ReadonlyArray<{ value: FormType; label: string }> = [
  { value: 'Generic', label: 'Generic' },
  { value: 'Qualifying Question', label: 'Qualifying Question' },
];
const META_AD_TYPE_OPTIONS: ReadonlyArray<{
  value: 'traffic' | 'lead_gen';
  label: string;
}> = [
  { value: 'traffic', label: 'Website Lead' },
  { value: 'lead_gen', label: 'Meta Lead Gen' },
];

type Props = {
  headers: string[];
  mapping: ColumnMappingState;
  onMappingChange: (next: ColumnMappingState) => void;
  bulkSet: BulkSetState;
  onBulkSetChange: (next: BulkSetState) => void;
};

// Apply a single dropdown change with the last-wins-with-silent-
// revert rule. Exported for the polish-round component tests in
// commit 7.
export function applyMappingChange(
  current: ColumnMappingState,
  header: string,
  value: SystemField | 'skip',
): ColumnMappingState {
  const next: ColumnMappingState = { ...current };
  if (value !== 'skip') {
    // Sweep any other header currently mapped to the same system
    // field and revert it to 'skip'. The dropdowns visibly update.
    for (const h of Object.keys(next)) {
      if (h !== header && next[h] === value) {
        next[h] = 'skip';
      }
    }
  }
  next[header] = value;
  return next;
}

// Fields the AM must satisfy before the Continue button unlocks.
// Brand is route-locked in this surface (the route already nails
// down the target brand) so doesn't appear here. Account name is
// interpreted as franchiseSuburb in the current data model —
// location names are server-computed from brand clubNamePrefix +
// franchiseSuburb. State is required because Meta naming templating
// reads {state}; missing it silently breaks downstream rendering.
type MandatoryField = { field: SystemField; label: string };
const MANDATORY_FIELDS: ReadonlyArray<MandatoryField> = [
  { field: 'franchiseSuburb', label: 'Suburb' },
  { field: 'state', label: 'State' },
  { field: 'groupRaw', label: 'Group' },
];

export type MappingValidation =
  | { ok: true }
  | { ok: false; missing: string[] };

// Returns 'ok' when every mandatory system field has at least one
// XLS column mapped to it via Table 1. Returns the friendly labels
// of any unsatisfied fields so the call site can render them in
// the disabled-button message.
//
// bulkSet isn't read today — none of the mandatory fields can be
// satisfied via Table 2 in the current data model (the enum
// bulk-set surface is Form Type / Meta Ad Type, neither of which
// is mandatory). The parameter is reserved for the future case
// where Brand becomes per-row and can be satisfied by either
// source.
export function validateMandatoryMappings(
  mapping: ColumnMappingState,
  _bulkSet: BulkSetState,
): MappingValidation {
  const mappedFields = new Set<SystemField>();
  for (const v of Object.values(mapping)) {
    if (v !== 'skip') mappedFields.add(v);
  }
  const missing: string[] = [];
  for (const { field, label } of MANDATORY_FIELDS) {
    if (!mappedFields.has(field)) missing.push(label);
  }
  return missing.length === 0 ? { ok: true } : { ok: false, missing };
}

export function MapColumnsStep({
  headers,
  mapping,
  onMappingChange,
  bulkSet,
  onBulkSetChange,
}: Props) {
  // Memoise the dropdown options once — the system field list is a
  // module-level constant, so this is purely cosmetic, but the
  // <option> array is stable across renders.
  const fieldOptions = useMemo(() => SYSTEM_FIELDS, []);

  function onPick(header: string, value: SystemField | 'skip') {
    onMappingChange(applyMappingChange(mapping, header, value));
  }

  function pickFormType(value: FormType | 'skip') {
    const next: BulkSetState = { ...bulkSet };
    if (value === 'skip') {
      delete next.defaultFormType;
    } else {
      next.defaultFormType = value;
    }
    onBulkSetChange(next);
  }

  function pickMetaAdType(value: 'traffic' | 'lead_gen' | 'skip') {
    const next: BulkSetState = { ...bulkSet };
    if (value === 'skip') {
      delete next.defaultMetaAdType;
    } else {
      next.defaultMetaAdType = value;
    }
    onBulkSetChange(next);
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Map XLS columns to system fields</h2>
          <p className="text-xs text-slate-500 mt-1">
            Auto-matched columns are pre-filled; correct anything wrong. Set to{' '}
            <em>Skip</em> to ignore a column on import.
          </p>
        </div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-medium w-1/2">XLS column</th>
              <th className="py-2 pr-4 font-medium">System field</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((header) => {
              const value: SystemField | 'skip' = mapping[header] ?? 'skip';
              return (
                <tr key={header} className="border-t border-slate-100 align-middle">
                  <td className="py-2 pr-4 font-mono text-xs text-slate-700">
                    {header.length > 0 ? `"${header}"` : <span className="italic text-slate-400">(blank header)</span>}
                  </td>
                  <td className="py-2 pr-4">
                    <select
                      className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      value={value}
                      onChange={(e) =>
                        onPick(header, e.target.value as SystemField | 'skip')
                      }
                      aria-label={`System field mapping for column ${header || '(blank)'}`}
                    >
                      <option value="skip">— Skip —</option>
                      {fieldOptions.map((f) => (
                        <option key={f} value={f}>
                          {SYSTEM_FIELD_LABELS[f]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Set defaults for missing fields</h2>
          <p className="text-xs text-slate-500 mt-1">
            These fields aren't in your XLS. Pick a default to apply to all rows, or
            leave unset.
          </p>
        </div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-medium w-1/2">System field</th>
              <th className="py-2 pr-4 font-medium">Bulk value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-100 align-middle">
              <td className="py-2 pr-4 text-slate-700">Default form type</td>
              <td className="py-2 pr-4">
                <select
                  className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={bulkSet.defaultFormType ?? 'skip'}
                  onChange={(e) => pickFormType(e.target.value as FormType | 'skip')}
                  aria-label="Bulk-set default form type"
                >
                  <option value="skip">— Skip —</option>
                  {FORM_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            <tr className="border-t border-slate-100 align-middle">
              <td className="py-2 pr-4 text-slate-700">Default Meta ad type</td>
              <td className="py-2 pr-4">
                <select
                  className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={bulkSet.defaultMetaAdType ?? 'skip'}
                  onChange={(e) =>
                    pickMetaAdType(e.target.value as 'traffic' | 'lead_gen' | 'skip')
                  }
                  aria-label="Bulk-set default Meta ad type"
                >
                  <option value="skip">— Skip —</option>
                  {META_AD_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
